import { getFormValues } from 'redux-form';
import { createSelector } from 'reselect';

import { RootState } from '../../reducers';
import { DeliveryInLotCreation, GroupInLotCreation, MovementInCreation } from './appLots.types';
import { Forms } from '../../form/form.selector';
import { numericStringComapreFunction } from '../../../core/helpers/utils/utils';

export const getGroupsInLot = (state: RootState): GroupInLotCreation[] => state.application.lots.groups;

export const getDisableReportFetching = (state: RootState): boolean => state.application.lots.disableReportFetching;

export const getDeliveries = (state: RootState): DeliveryInLotCreation[] =>
  state.application.lots.deliveries.sort((d1, d2) => d1.date.getTime() - d2.date.getTime());

export const getLotEditorFormValues = (state: RootState) => getFormValues(Forms.lotsEditorForm)(state);

export const isGroupsExists = createSelector([getGroupsInLot], (groups): boolean => {
  return groups.length > 0;
});

export const isGroupsValid = createSelector([getGroupsInLot], (groups): boolean => {
  return !groups.some((x) => x.weightIn <= 0 || x.headCountIn <= 0);
});

export const getArrivals = createSelector([getGroupsInLot], (groups) => {
  return groups.map((x) => ({
    avgWeight: x.weightIn,
    timestamp: x.arrivalDate,
    headCount: x.headCountIn,
    movementTypeCode: 1,
    toGroupId: x.groupId,
  }));
});

export const getMovements = (state: RootState): MovementInCreation[] => state.application.lots.movements;

export const getMovementsByLotId = createSelector(
  [getMovements, (_state: RootState, lotId: string) => lotId],
  (movements, lotId) => movements.filter((x) => x.lotId === lotId),
);

export const getMovementsByGroupId = createSelector(
  [getMovements, (_state: RootState, groupId: string) => groupId],
  (movements, groupId) => movements.filter((x) => x.fromGroupId === groupId),
);

export const isTransfersValid = createSelector(
  [getMovements],
  (transfers) => transfers.length && !transfers.some((x) => x.headCount <= 0 || Number(x.toGroupId) === -1),
);

export const isDispatchesValid = createSelector(
  [getMovements],
  (transfers) =>
    transfers.length && !transfers.some((x) => x.headCount <= 0 || (x.avgWeight !== undefined && x.avgWeight <= 0)),
);

export const getCloseoutPreviewPDF = (state: RootState) => state.application.lots.closeoutPreview.pdfData;

export const getIncludedCloseouts = (state: RootState) => state.application.lots.closeoutPreview.includedCloseouts;
export const getIncludedCloseoutsExisting = (state: RootState) =>
  state.application.lots.closeoutPreview.includedCloseoutsExisting;

export const getTransfersHistoryLot = (state: RootState) => state.application.lots.transfersHistory.lotPreview;
export const getTransfersHistoryChanged = (state: RootState) => state.application.lots.transfersHistory.changed;
export const getTransfersHistoryAddedMovements = (state: RootState) =>
  state.application.lots.transfersHistory.newMovements;
export const transfersHistoryLotIsEdit = (state: RootState) => state.application.lots.transfersHistory.isEdit;
export const getAutoExpandedAdditionalCostId = (state: RootState) => state.application.lots.autoExpandedAdditionalCost;
export const getCostSummaryReport = (state: RootState) => state.application.lots.costSummaryReport;
export const getCostMovementsReport = (state: RootState) => state.application.lots.costMovements;

export const getCostSummaryReportData = createSelector(getCostSummaryReport, (report) => {
  if (!report?.reportData?.gridData?.items) {
    return null;
  }
  const { items, groupSummaries } = report.reportData.gridData;
  const filteredItems = [...items];
  filteredItems.sort((item1, item2) => numericStringComapreFunction(item1.name, item2.name));
  const data = items.map((item) => {
    const groupSummary = groupSummaries?.find((summary) => summary.key === item.typeId);
    return {
      ...item,
      ...groupSummary?.items[0],
    };
  });
  return data;
});

export const getCostSummaryReportCosts = createSelector(getCostSummaryReport, (report) => {
  if (!report?.reportData) {
    return { feedCosts: null, otherCosts: null, totalCosts: null };
  }

  const { feedCosts, otherCosts, totalCosts } = report.reportData;
  return { feedCosts, otherCosts, totalCosts };
});

export const getCostTypeDetailById = createSelector(getCostSummaryReport, (report) => (id: string) => {
  const reportDetails = [
    ...(report?.reportData?.detailGridDataLot?.items || []),
    ...(report?.reportData?.detailGridDataGroup?.items || []),
  ];
  return reportDetails.filter((item) => item.typeId === id);
});

export const getGroupSummaryByKey = createSelector(
  getCostSummaryReport,
  (report) => (key: string) =>
    report?.reportData.detailGridDataGroup?.groupSummaries?.find((summary) => summary.key === key),
);

export const getCostMovements = createSelector(getCostMovementsReport, (report) => {
  if (!report?.reportData?.gridData?.items) {
    return null;
  }
  const data = [...report.reportData.gridData.items];
  data.sort((item1, item2) => new Date(item1.timestamp).getTime() - new Date(item2.timestamp).getTime());
  return data;
});
